/**
 * PageContainer — in-page vertical rhythm + full-bleed / viewport-lock shells.
 *
 * The root route already provides outer padding, max-width and centering.
 * This wrapper standardises what happens *inside* `<Outlet/>`:
 *
 *  - `default`       standard scrolling page; `spacing` controls the gap
 *                    between direct children (canonical detail rhythm is
 *                    `space-y-6`).
 *  - `full-bleed`    fills available flex height. Use only when an ancestor
 *                    is height-constrained (e.g. inside a `viewport-lock`).
 *  - `viewport-lock` a viewport-anchored chat/canvas shell with its OWN
 *                    internal scroll (Agent / Ask Canvas surfaces).
 *
 * Why `viewport-lock` exists: these surfaces need a hard viewport ceiling for their own inner
 * scroll, and __root's page column is not one. Its height is definite (`html`/`body`/`#root` are
 * `height: 100%`, so `h-full` and `flex-1` do resolve against it; see `AppPageColumn`), but the
 * column has `flex-1` without `min-h-0`, so it grows with its content instead of capping it.
 * Cancelling <main>'s padding with negative margins is fragile and fights the sticky z-40
 * navbar. Instead we position the pane against the viewport, mirroring
 * __root's chrome offsets exactly:
 *   - top-14            → __root Navbar is h-14, sticky
 *   - min-[901px] left  → __root content column is ml-[232px] ≥901px
 *                          (fixed sidebar width); full-bleed below that
 *   - right-0 bottom-0  → fill to the viewport edges
 *   - z-0               → stay under the z-40 navbar and the sidebar
 * Result: header always sits flush under the navbar, only the inner stream
 * scrolls, and there is zero document scroll regardless of content length.
 */
import type { ElementType, ReactNode } from 'react'
import { cn } from '@/lib/cn'

const SPACING: Record<'default' | 'tight' | 'none', string> = {
  default: 'space-y-6',
  tight: 'space-y-4',
  none: '',
}

const VARIANT: Record<'full-bleed' | 'viewport-lock', string> = {
  'full-bleed': 'flex flex-col flex-1 min-h-0',
  'viewport-lock': cn(
    'fixed top-14 right-0 bottom-0 left-0 z-0',
    'min-[901px]:left-[232px]',
    'flex flex-col overflow-hidden bg-bg',
  ),
}

export interface PageContainerProps {
  children: ReactNode
  /** Gap between direct children. `default` = space-y-6 (canonical). */
  spacing?: 'default' | 'tight' | 'none'
  /**
   * `default` — standard scrolling page (uses `spacing`).
   * `full-bleed` — fills available flex height (needs a height-constrained ancestor).
   * `viewport-lock` — viewport-anchored shell with its own internal scroll.
   */
  variant?: 'default' | 'full-bleed' | 'viewport-lock'
  /** Render element. Defaults to `div`. */
  as?: ElementType
  className?: string
  /** Test hook passthrough (kept explicit so the prop stays typed). */
  'data-testid'?: string
}

export function PageContainer({
  children,
  spacing = 'default',
  variant = 'default',
  as,
  className,
  'data-testid': testId,
}: PageContainerProps) {
  const Component = as ?? 'div'
  return (
    <Component
      className={cn(
        variant === 'default' ? SPACING[spacing] : VARIANT[variant],
        className,
      )}
      data-testid={testId}
    >
      {children}
    </Component>
  )
}
